// Battery Status API wrapper for WIN Field App.
//
// Provides battery snapshots and background monitoring (charging state changes,
// low/critical threshold crossings). Android only -- returns null on iOS/Firefox
// where navigator.getBattery is unavailable.
//
// Usage:
//   import { getBatterySnapshot, initBatteryMonitor } from "./battery_monitor"
//   await initBatteryMonitor()  // sets up charging + threshold listeners
//   const snap = await getBatterySnapshot()  // { level, charging, ... } or null

import { logEvent, logWarn } from "./field_logger"
import { foregroundTimeStamp } from "./field_foreground_time"

// Settings > Diagnostics toggle (cards #4143, #4153).
//
// The choice is stored here as "true" / "false" next to the time it was made,
// and mirrored to the server (field_settings_sync.js, user_settings) so head
// office can read it. With NO stored choice the default depends on the phone:
// on where the Battery API exists (Android Chrome), off on iPhone, where "on"
// means the 30 minute battery check-in prompt. An explicit choice always wins,
// whether the Tasker made it or head office did (adopted on sync).
export const BATTERY_MONITOR_KEY = "field_battery_monitoring"
export const BATTERY_MONITOR_CHANGED_AT_KEY = "field_battery_monitoring_changed_at"

export function batteryApiAvailable() {
  return typeof navigator !== "undefined" && typeof navigator.getBattery === "function"
}

export function isIPhone() {
  return typeof navigator !== "undefined" && /iPhone/.test(navigator.userAgent)
}

// The setting only means something where there is a Battery API or an iPhone to
// ask; the Settings row is hidden anywhere else.
export function batteryMonitoringAvailable() {
  return batteryApiAvailable() || isIPhone()
}

// "true", "false", or null when the Tasker has made no choice yet.
export function batteryMonitoringChoice() {
  try {
    const stored = localStorage.getItem(BATTERY_MONITOR_KEY)
    return stored === "true" || stored === "false" ? stored : null
  } catch {
    return null
  }
}

export function batteryMonitoringEnabled() {
  const choice = batteryMonitoringChoice()
  if (choice !== null) return choice === "true"
  return batteryApiAvailable()
}

let batteryManager = null
let managerResolved = false
let monitorInitialised = false
let lastChargingState = null
let lowAlerted = false
let criticalAlerted = false

const LOW_THRESHOLD      = 0.15
const CRITICAL_THRESHOLD = 0.05

// Returns a battery snapshot object or null if unsupported.
export async function getBatterySnapshot() {
  const mgr = await _getManager()
  if (!mgr) return null

  return {
    level:           Math.round(mgr.level * 100),
    charging:        mgr.charging,
    chargingTime:    mgr.chargingTime,
    dischargingTime: mgr.dischargingTime
  }
}

// Initialise background battery monitoring.
// Safe to call multiple times -- listeners are only attached once.
export async function initBatteryMonitor() {
  if (monitorInitialised) return
  monitorInitialised = true

  const mgr = await _getManager()
  if (!mgr) return

  // Seed initial state so the first chargingchange fires correctly.
  lastChargingState = mgr.charging
  lowAlerted = mgr.level <= LOW_THRESHOLD
  criticalAlerted = mgr.level <= CRITICAL_THRESHOLD

  // Switched off while the manager was resolving: leave the listeners off.
  if (!monitorInitialised) return

  mgr.addEventListener("chargingchange", _onChargingChange)
  mgr.addEventListener("levelchange", _onLevelChange)
}

// Stop background monitoring (the Settings toggle was switched off).
// initBatteryMonitor() can start it again.
export function stopBatteryMonitor() {
  monitorInitialised = false
  if (!batteryManager) return

  batteryManager.removeEventListener("chargingchange", _onChargingChange)
  batteryManager.removeEventListener("levelchange", _onLevelChange)
}

// -- private --

async function _getManager() {
  if (managerResolved) return batteryManager
  managerResolved = true

  if (typeof navigator.getBattery !== "function") return null

  try {
    batteryManager = await navigator.getBattery()
  } catch {
    batteryManager = null
  }
  return batteryManager
}

function _onChargingChange() {
  if (!batteryManager) return
  const charging = batteryManager.charging
  if (charging === lastChargingState) return
  lastChargingState = charging

  const level = Math.round(batteryManager.level * 100)
  const message = charging ? "Charging started" : "Charging stopped"
  logEvent("battery", message, { level, ...foregroundTimeStamp() }).catch(() => {})
}

function _onLevelChange() {
  if (!batteryManager) return
  const level = batteryManager.level

  if (level <= CRITICAL_THRESHOLD && !criticalAlerted) {
    criticalAlerted = true
    logWarn("battery", "Critical battery (5%)", { level: Math.round(level * 100) }).catch(() => {})
  } else if (level <= LOW_THRESHOLD && !lowAlerted) {
    lowAlerted = true
    logWarn("battery", "Low battery (15%)", { level: Math.round(level * 100) }).catch(() => {})
  }

  // Reset alerts when battery recovers above thresholds
  if (level > LOW_THRESHOLD) {
    lowAlerted = false
    criticalAlerted = false
  } else if (level > CRITICAL_THRESHOLD) {
    criticalAlerted = false
  }
}
